import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { supabase } from '../supabaseClient';
import DataTable from './DataTable';
import { calculateAge } from '../utils';
import { ClipLoader } from 'react-spinners';

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseDetail, setCaseDetail] = useState(null);
  const [imageUrls, setImageUrls] = useState([]);
  const [loadingImages, setLoadingImages] = useState(true);

  // Get user_id from URL query params
  const searchParams = new URLSearchParams(window.location.search);
  const user_id = searchParams.get('user_id');

  const fetchCaseDetail = async () => {
    try {
      const { data: caseDetail, error } = await supabase
        .from('visits')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      setCaseDetail(caseDetail);

      // Use user_id for folder path and id for image names
      const { data: listData, error: listError } = await supabase.storage
        .from('pixcareimages')
        .list(user_id + '/', {
          limit: 1000,
        });

      console.log('listData', listData, user_id, id);
      if (listError) throw listError;
      
      const capitalizedID = id.toUpperCase();
      const imagePromises = listData
        .filter(item => item.name.includes(capitalizedID)) // Filter images that include the visit id
        .map(async (item, index) => {
          console.log('item', item);
          const { data, error } = await supabase.storage
            .from('pixcareimages')
            .download(user_id.toUpperCase() + '/' + item.name);
          if (error) {
            console.error('Error fetching image:', error);
            return null;
          }

          const stream = data.stream();
          return { stream, index };
        });

  
      const imageStreams = await Promise.all(imagePromises);
      imageStreams.forEach(({ stream, index }) => {
        if (stream) {
          loadImage(stream, index);
        }
      });
      setLoadingImages(false);

    } catch (error) {
      console.error('Error fetching case detail:', error);
    }
  };

  useEffect(() => {
    fetchCaseDetail();
  }, [id]);

  const loadImage = (stream, index) => {
    const reader = stream.getReader();
    let chunks = [];

    reader.read().then(function processText({ done, value }) {
      if (done) {
        const blob = new Blob(chunks);
        const url = URL.createObjectURL(blob);
        setImageUrls((prev) => {
          const newImageUrls = [...prev];
          newImageUrls[index] = url;
          return newImageUrls;
        });
        return;
      }

      chunks.push(value);
      const blob = new Blob(chunks);
      const url = URL.createObjectURL(blob);
      setImageUrls((prev) => {
        const newImageUrls = [...prev];
        newImageUrls[index] = url;
        return newImageUrls;
      });

      reader.read().then(processText);
    });
  };

  const columnHelper = createColumnHelper();

  const patientColumns = useMemo(
    () => [
      columnHelper.accessor('field1', {
        header: '',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('value1', {
        header: '',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('field2', {
        header: '',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('value2', {
        header: '',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('field3', {
        header: '',
        cell: info => info.getValue(),
      }),
      columnHelper.accessor('value3', {
        header: '',
        cell: info => info.getValue(),
      }),
    ],
    [columnHelper]
  );

  const patientData = useMemo(() => {
    if (!caseDetail) return [];
    return [
      { field1: 'Study ID', value1: caseDetail.studyID, field2: 'Age', value2: calculateAge(caseDetail.dob), field3: 'Gender', value3: caseDetail.gender },
      { field1: 'Race', value1: caseDetail.race, field2: 'Ethnicity', value2: caseDetail.ethnicity, field3: 'Insurance', value3: caseDetail.insurance },
      { field1: 'Location', value1: caseDetail.location, field2: 'Comment', value2: caseDetail.comment, field3: 'Molecular Assay', value3: caseDetail.molecularAssay },
      { field1: 'Rapid Test', value1: caseDetail.rapidTest, field2: 'Culture Test', value2: caseDetail.cultureTest, field3: 'Known Recent Contact', value3: caseDetail.knownRecentContact },
      { field1: 'History of Tonsillectomy', value1: caseDetail.historyOfTonsillectomy, field2: 'Antibiotics In Last 48 Hours', value2: caseDetail.antibioticsInLast48Hours, field3: '', value3: '' },
    ];
  }, [caseDetail]);

  const symptomData = useMemo(() => {
    if (!caseDetail) return [];
    return [
      { field1: 'Sore Throat', value1: caseDetail.soreThroat, field2: 'Fever', value2: caseDetail.fever, field3: 'Headache', value3: caseDetail.headache },
      { field1: 'Abdominal Pain', value1: caseDetail.abdominalPain, field2: 'Difficulty Swallowing', value2: caseDetail.difficultySwallowing, field3: 'Cough', value3: caseDetail.cough },
      { field1: 'Congestion', value1: caseDetail.congestion, field2: 'Rhinorrhea', value2: caseDetail.rhinorrhea, field3: 'Hoarseness', value3: caseDetail.hoarseness },
      { field1: 'Conjunctivitis', value1: caseDetail.conjunctivitis, field2: 'Enlarged Cervical Lymph Nodes', value2: caseDetail.enlargedCervicalLymphNodes, field3: 'Tender Cervical Lymph Nodes', value3: caseDetail.tenderCervicalLymphNodes },
      { field1: 'Known Recent Contact', value1: caseDetail.knownRecentContact, field2: 'History of Tonsillectomy', value2: caseDetail.historyOfTonsillectomy, field3: 'Antibiotics In Last 48 Hours', value3: caseDetail.antibioticsInLast48Hours },
    ];
  }, [caseDetail]);

  if (!caseDetail) return <div>Loading...</div>;

  return (
    <div className="container mx-auto p-8">
      <button
        onClick={() => navigate(-1)}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-700"
      >
        Back
      </button>
      <h1 className="text-3xl font-bold mb-8">Case Detail</h1>

      <div className="bg-white p-6 rounded shadow-md">
        <h2 className="text-2xl font-bold mb-4">Patient Information</h2>
        <DataTable columns={patientColumns} data={patientData} />
      </div>

      <div className="bg-white p-6 rounded shadow-md mt-8">
        <h2 className="text-2xl font-bold mb-4">Symptoms</h2>
        <DataTable columns={patientColumns} data={symptomData} />
      </div>

      <div className="bg-white p-6 rounded shadow-md mt-8">
        <h2 className="text-2xl font-bold mb-4">Images</h2>
        <div className="grid grid-cols-3 gap-4">
          {loadingImages ? (
            <ClipLoader color="#000" loading={loadingImages} size={50} />
          ) : (
            imageUrls.map((url, index) => (
              <img key={index} src={url} alt={`Image ${index + 1}`} className="w-full h-auto" />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseDetail;
